import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Observable, Subject, distinctUntilChanged, firstValueFrom, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { EnfafCheckboxComponent } from '../../../../../components/enfaf-checkbox/enfaf-checkbox.component';
import { EnfafExpansionPanelLightComponent } from '../../../../../components/enfaf-expansion-panel-light/enfaf-expansion-panel-light.component';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafImgDraggerComponent } from '../../../../../components/enfaf-img-dragger/enfaf-img-dragger.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafPdfUploadComponent, InitialPdfFile, PdfFile } from '../../../../../components/enfaf-pdf-upload/enfaf-pdf-upload.component';
import { EnfafSelectLightComponent } from '../../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EnfafSwitchDarkComponent } from '../../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { EnfafTextareaLightComponent } from '../../../../../components/enfaf-textarea-light/enfaf-textarea-light.component';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { KommoService } from '../../../../../core/services/kommo/kommo.service';
import { SettingsService } from '../../../../../core/services/settings/settings.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { pipelineAsyncValidator } from '../../validators/pipeline-async.validator';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  CourseEditionData,
  CourseEditionDefaults,
  CourseEditionPayload,
  CourseFieldError
} from '../../../../../models/interfaces/course-edition.interface';
import { CourseEditionFieldsComponent, EditionDateValue } from './course-edition/course-edition-fields.component';
import { FieldErrorA11yDirective } from './course-edition/field-error-a11y.directive';
import {
  COURSE_NAME_INPUT_MAXLENGTH,
  COURSE_NAME_MAX_LENGTH,
  MASTER_NAME_INPUT_MAXLENGTH,
  MASTER_NAME_MAX_LENGTH,
  buildEditionName,
  codePointLength,
  editionNumberValidator,
  isCourseDeletedError,
  isPositiveInteger,
  maxCodePointsValidator,
  parseEditionNumber,
  pickerValueToUtcIso,
  stripEditionSuffix,
  utcIsoToPickerValue,
  validityRangeValidator,
  validityYearValidator
} from './course-edition/course-edition.utils';
import { NETWORK_ERROR_KEY, isNetworkError } from '../../../../../shared/utils/http-error.util';
import { MessageDto } from '../../../../../models/dto/message.dto';
import { CourseModality } from '../../../../../models/enums/course-mode.enum';
import { EmbassadorComissionType } from '../../../../../models/enums/embassador_commission_type.enum';
import { MessageSendCondition } from '../../../../../models/enums/message_send_condition.enum';
import { Role } from '../../../../../models/enums/role.enum';
import { CourseEquivalence } from '../../../../../models/interfaces/course.interface';
import { formatRole } from '../../../../../shared/utils/format-role.util';
import { permissiveUrlValidator } from '../../../../../shared/validators/permisive-url.validator';

function httpsOnlyUrlValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') {
      return { httpsRequired: true };
    }
    return null;
  } catch {
    return { invalidUrl: true };
  }
}

const MODALITY_LABEL_KEYS_BY_VALUE: Record<CourseModality, string> = {
  [CourseModality.STANDARD]: 'COURSE.MODALITY.STANDARD',
  [CourseModality.FLASH]: 'COURSE.MODALITY.FLASH',
  [CourseModality.FP]: 'COURSE.MODALITY.FP',
  [CourseModality.MASTER]: 'COURSE.MODALITY.MASTER'
};
const MODALITY_LABEL_KEYS = Object.values(MODALITY_LABEL_KEYS_BY_VALUE);

@Component({
  selector: 'app-modal-edit-course',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatSlideToggleModule,
    EnfafModalComponent,
    MatExpansionModule,
    EnfafExpansionPanelLightComponent,
    EnfafImgDraggerComponent,
    EnfafInputComponent,
    EnfafCheckboxComponent,
    EnfafIconComponent,
    MatTooltipModule,
    EnfafSelectLightComponent,
    EnfafTextareaLightComponent,
    MultiAutocompleteLightComponent,
    EnfafSwitchDarkComponent,
    EnfafPdfUploadComponent,
    TranslatePipe,
    CourseEditionFieldsComponent,
    FieldErrorA11yDirective
  ],
  templateUrl: './modal-edit-course.component.html',
  styleUrl: './modal-edit-course.component.scss'
})
export class ModalEditCourseComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('teachersAutocomplete') teachersAutocomplete?: MultiAutocompleteLightComponent;
  @ViewChild('contentDirectorAutocomplete') contentDirectorAutocomplete?: MultiAutocompleteLightComponent;

  isCourseInfoExpanded = true;
  isDraggingOver = false;

  courseForm!: FormGroup;
  showCloseAlert = false;
  isSavingCourse = false;
  /** A save is running, from the click to its answer (see saveCourse). */
  private saveInProgress = false;
  formModified = false;
  editMode = false;
  isLoadingCourse = true;
  course: any = null;
  /** Valor cargado del curso, para saber si el admin ha cambiado el flag al guardar. */
  private initialGenerateAutoevaluation = true;
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;
  competenciesFileToUpload: File | null = null;
  competenciesInitialFile: InitialPdfFile | null = null;
  /** Debe coincidir con el límite del backend (MAX_COMPETENCIES_FILE_BYTES). */
  readonly competenciesMaxFileSize = 10 * 1024 * 1024;
  selectedTeachersAndTutors: SelectOption[] = [];
  selectedContentDirectors: SelectOption[] = [];
  openedCourseMessages: Map<string, { templateBody: string }> = new Map();
  allMessages: MessageDto[] = [];

  /** «Crear nueva edición»: edition-defaults and the prefill from the root are loading. */
  isLoadingPredecessorPrefill = false;
  /**
   * The modal creates a new edition («Crear nueva edición», only God). Its automatic course messages are not sent:
   * the server discards them and the edition inherits the ones of its root, live (C-6).
   */
  isCreatingEdition = false;

  // ENF-2372 — ediciones de un Máster (modelo v2, decisiones del PO del 15-oct).
  readonly courseNameMaxLength = COURSE_NAME_MAX_LENGTH;
  /** Nombre del Máster de la raíz: base del nombre generado `{masterName} - {n}ª Edición` de una edición (A.8). */
  editionRootName: string | null = null;
  /**
   * Modalidad bloqueada: no cambia en ningún curso ya creado (ENF-2372 v2, D2), ni en la edición que se crea con
   * «Crear nueva edición» (siempre Máster).
   */
  modalityLocked = false;
  /** Modalidad anterior del selector (solo cambia al crear un curso): para pasar el nombre entre «Nombre del curso» y «Nombre del Máster». */
  private previousModality: CourseModality | null = null;

  // Original yearsCount as loaded from the backend, used to detect a reduction
  // (US A.3) before submitting — see saveCourse().
  private originalYearsCount: number | null = null;

  /** Opciones de modalidad con etiquetas traducidas; ver buildModalityOptions(). */
  courseModalityOptions: Array<{ value: CourseModality; viewValue: string }> = [];

  requieredDocumentsOptions = [
    {
      value: null,
      viewValue: '-'
    },
    {
      value: 'before',
      viewValue: 'Antes de comenzar el curso'
    },
    {
      value: 'after',
      viewValue: 'Al finalizar el curso'
    }
  ];

  ambassadorCommissionTypeOptions = Object.values(EmbassadorComissionType).map((v) => ({
    value: v,
    viewValue: this.mappEmbassadorComissionTypeToViewValue(v)
  }));

  equivalentMasterOptions: Array<{ value: string | null; viewValue: string }> = [{ value: null, viewValue: '-' }];
  equivalentCourseOptions: Array<{ value: string | null; viewValue: string }> = [{ value: null, viewValue: '-' }];

  private unsubscribe$ = new Subject<void>();
  private destroyed$ = new Subject<void>();

  // Custom validators for commissions
  static percentageCommissionValidator(control: any): any {
    const value = control.value;
    if (value === null || value === '') return null;
    if (value < 0 || value > 100) {
      return { percentageRange: { message: 'El porcentaje debe estar entre 0 y 100' } };
    }
    if (value % 1 !== 0) {
      return { noDecimals: { message: 'No se permiten decimales para porcentajes' } };
    }
    return null;
  }

  static fixedCommissionValidator(control: any): any {
    const value = control.value;
    if (value === null || value === '') return null;
    if (value < 0) {
      return { negativeValue: { message: 'El valor no puede ser negativo' } };
    }
    return null;
  }

  get modalityFC(): FormControl {
    return this.courseForm.get('modality') as FormControl;
  }

  get durationFC(): FormControl {
    return this.courseForm.get('duration') as FormControl;
  }

  get yearsCountFC(): FormControl {
    return this.courseForm.get('yearsCount') as FormControl;
  }

  /** Años de duración (FP) must be a plain positive integer — blocks '.', '-', 'e', letters, etc. that a native type="number" input still lets through on most browsers, while leaving navigation/editing keys and copy-paste shortcuts untouched. */
  blockNonNumericKey(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  get isFlash(): boolean {
    return this.modalityFC.value === CourseModality.FLASH;
  }

  get isFP(): boolean {
    return this.modalityFC.value === CourseModality.FP;
  }

  get isGod(): boolean {
    return this.auth.isGod();
  }

  get nameFC(): FormControl<string | null> {
    return this.courseForm.get('name') as FormControl<string | null>;
  }

  get masterNameFC(): FormControl<string | null> {
    return this.courseForm.get('masterName') as FormControl<string | null>;
  }

  get isSuccessorFC(): FormControl<boolean | null> {
    return this.courseForm.get('isSuccessor') as FormControl<boolean | null>;
  }

  get predecessorFC(): FormControl<string | null> {
    return this.courseForm.get('predecessorCourseId') as FormControl<string | null>;
  }

  get editionNumberFC(): FormControl<number | string | null> {
    return this.courseForm.get('editionNumber') as FormControl<number | string | null>;
  }

  get validFromFC(): FormControl<EditionDateValue> {
    return this.courseForm.get('purchaseValidFrom') as FormControl<EditionDateValue>;
  }

  get validUntilFC(): FormControl<EditionDateValue> {
    return this.courseForm.get('purchaseValidUntil') as FormControl<EditionDateValue>;
  }

  /** Máster sin predecesor: la raíz, que es su 1ª edición (v2, D3). */
  get isMasterRoot(): boolean {
    return this.modalityFC?.value === CourseModality.MASTER && !this.predecessorFC?.value;
  }

  /** Máster con predecesor: una edición (la que se crea con «Crear nueva edición» o una ya guardada). */
  get isEditionCourse(): boolean {
    return this.modalityFC?.value === CourseModality.MASTER && !!this.predecessorFC?.value;
  }

  /** Bloque de la edición (interruptor y raíz bloqueados, número editable): solo God (D-10). */
  get showEditionControls(): boolean {
    return this.isGod && this.isEditionCourse;
  }

  /** Vigencia visible en cualquier Máster, raíz o edición (A.3). Solo God. */
  get showEditionValidity(): boolean {
    return this.isGod && this.modalityFC?.value === CourseModality.MASTER;
  }

  /** El campo «Edición» está en pantalla. */
  get isEditionNumberVisible(): boolean {
    return this.showEditionControls;
  }

  /** Obligatoria solo al crear una edición nueva (A.3, D-3); opcional en la raíz y en una edición ya guardada. */
  get editionValidityRequired(): boolean {
    return !this.editMode && this.isEditionCourse;
  }

  /** El nombre de una edición lo genera el sistema y no se teclea (A.8). */
  get isEditionNameLocked(): boolean {
    return this.isEditionCourse;
  }

  /** Solo God crea o renombra el Máster (decisión del PO del 15-oct); un tutor lo ve de solo lectura. */
  get canEditMasterName(): boolean {
    return this.isGod;
  }

  get editionNameTooLong(): boolean {
    return this.isEditionNameLocked && codePointLength(this.nameFC.value) > COURSE_NAME_MAX_LENGTH;
  }

  /**
   * `maxlength` nativo del campo nombre: holgado (2 × 100) para que el navegador no corte en silencio un nombre válido
   * pegado de una vez (E-D1). El límite de 100 caracteres lo impone `maxCodePointsValidator`, con su mensaje.
   */
  readonly nameMaxLengthAttr = COURSE_NAME_INPUT_MAXLENGTH;
  /** Lo mismo para «Nombre del Máster» (límite real: 86 caracteres). */
  readonly masterNameMaxLengthAttr = MASTER_NAME_INPUT_MAXLENGTH;

  get masterNameErrorLabel(): string {
    const control = this.masterNameFC;
    if (!control) return '';
    if (control.hasError('serverError')) return String(control.getError('serverError'));
    if (control.hasError('maxlength')) return this.translate.instant('COURSE.EDITION.ERRORS.NAME_TOO_LONG', { max: MASTER_NAME_MAX_LENGTH });
    if (control.hasError('required')) return this.translate.instant('COURSE.EDITION.ERRORS.MASTER_NAME_REQUIRED');
    return '';
  }

  /** Bajo «Nombre del Máster»: el nombre completo de la 1ª edición y, al editar, que renombrarlo renombra el grupo. */
  get masterNameHint(): string {
    const fullName = this.translate.instant('COURSE.EDITION.MASTER_NAME_HINT', { name: this.nameFC.value || '—' });
    return this.editMode && this.canEditMasterName ? `${fullName} ${this.translate.instant('COURSE.EDITION.MASTER_NAME_RENAME_HINT')}` : fullName;
  }

  get nameErrorLabel(): string {
    const control = this.nameFC;
    if (!control) return '';
    if (control.hasError('serverError')) return String(control.getError('serverError'));
    if (control.hasError('maxlength')) return this.translate.instant('COURSE.EDITION.ERRORS.NAME_TOO_LONG', { max: COURSE_NAME_MAX_LENGTH });
    if (control.hasError('required')) return this.translate.instant('COURSE.EDITION.ERRORS.NAME_REQUIRED');
    return '';
  }

  get courseMessagesFA(): FormArray {
    return this.courseForm.get('courseMessages') as FormArray;
  }

  get courseMessagesControls(): FormGroup[] {
    return this.courseMessagesFA.controls as FormGroup[];
  }

  /**
   * An existing edition without course messages of its own uses the ones of its root, live (C-6): the panel stays
   * editable, and saving its own messages makes it use only those.
   */
  get inheritsRootMessages(): boolean {
    return this.editMode && this.isEditionCourse && !this.course?.courseMessages?.length;
  }

  get autoMessagesEnabled(): boolean {
    return this.courseForm.get('autoMessagesEnabled')?.value;
  }

  get isTutor(): boolean {
    return this.auth.isTutor();
  }

  get requiresDegreeMissingEquivalents(): boolean {
    if (this.isFlash) return false;

    const requiresDegree = this.courseForm.get('requiresDegree')?.value;
    if (!requiresDegree) return false;
    const equivalentMasterId = this.courseForm.get('equivalentMasterId')?.value;
    const equivalentCourseId = this.courseForm.get('equivalentCourseId')?.value;
    return !equivalentMasterId || !equivalentCourseId;
  }

  get showDegreeRequirements(): boolean {
    return !this.isFlash && this.courseForm.get('requiresDegree')?.value === true;
  }

  get kommoEnabled(): boolean {
    return this.settingsService.settings?.kommoEnabled === true;
  }

  get pipelineErrorLabel(): string {
    const control = this.courseForm.get('pipelineId');
    if (!control) return '';
    if (control.hasError('pipelineInvalid')) return 'Pipeline no encontrada en Kommo';
    if (control.hasError('pipelineValidationError')) return 'Error de conexión con Kommo';
    if (control.hasError('pattern')) return 'El ID debe ser numérico';
    return '';
  }

  get isAssembadorProgramEnabled(): boolean {
    return this.courseForm.get('isAmbassadorProgramEnabled')?.value;
  }

  get ambassadorCommissionTypeFC(): FormControl {
    return this.courseForm.get('ambassadorCommissionType') as FormControl;
  }

  get ambassadorCommissionValueFC(): FormControl {
    return this.courseForm.get('ambassadorCommissionValue') as FormControl;
  }

  get ambassadorCommissionValueError(): string {
    const control = this.ambassadorCommissionValueFC;
    if (control.hasError('required')) {
      return 'Este campo es requerido';
    }
    if (control.hasError('percentageRange')) {
      return control.getError('percentageRange').message;
    }
    if (control.hasError('noDecimals')) {
      return control.getError('noDecimals').message;
    }
    if (control.hasError('negativeValue')) {
      return control.getError('negativeValue').message;
    }
    return '';
  }

  get assambladorCommissionIcon(): { icon: string; color: string; click: () => void } | undefined {
    const type = this.courseForm.get('ambassadorCommissionType')?.value;
    if (type === EmbassadorComissionType.FIXED) {
      return { icon: 'euro-sign', color: 'dark', click: () => {} };
    }
    if (type === EmbassadorComissionType.PERCENTAGE) {
      return { icon: 'percent', color: 'dark', click: () => {} };
    }
    return undefined;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditCourseComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private auth: AuthService,
    private kommoService: KommoService,
    private settingsService: SettingsService,
    private translate: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.translate
      .stream(MODALITY_LABEL_KEYS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.buildModalityOptions());

    // Only administrators should load WhatsApp messages
    if (this.isGod) {
      await this.fetchMessages();
    }
    await this.loadCourseEquivalences();
    this.initForm();
  }

  private async loadCourseEquivalences(): Promise<void> {
    try {
      const [mastersRes, coursesRes] = await Promise.all([
        firstValueFrom(this.api.fetchCourseEquivalences('master')),
        firstValueFrom(this.api.fetchCourseEquivalences('course'))
      ]);
      this.equivalentMasterOptions = [
        { value: null, viewValue: '-' },
        ...(mastersRes.equivalences || []).map((e: CourseEquivalence) => ({ value: e.courseEquivalenceId, viewValue: e.name }))
      ];
      this.equivalentCourseOptions = [
        { value: null, viewValue: '-' },
        ...(coursesRes.equivalences || []).map((e: CourseEquivalence) => ({ value: e.courseEquivalenceId, viewValue: e.name }))
      ];
    } catch (_error) {
      this.toast.presentErrorToast('Hubo un error cargando las equivalencias de título');
    }
  }

  private async fetchMessages(): Promise<void> {
    const response = await firstValueFrom(this.api.fetchMessages({ title: '', active: true }));
    if (response && response.data && response.data.messages && response.data.messages.length > 0) {
      this.allMessages = response.data.messages;
    }
  }

  ngAfterViewInit(): void {
    // setTimeout(() => {
    //   this.syncTeachersWithAutocomplete();
    //   this.syncContentDirectorsWithAutocomplete();
    // }, 100);
  }

  onTeachersSelectionChanged(selected: SelectOption[]): void {
    this.selectedTeachersAndTutors = selected;
    this.markAsModified();
  }

  onContentDirectorsSelectionChanged(selected: SelectOption[]): void {
    this.selectedContentDirectors = selected;
    this.markAsModified();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Construye las opciones de modalidad (ENF-2372, D-10/D-14): etiquetas traducidas;
   * «Máster» solo se ofrece a God, salvo que el curso editado ya sea máster.
   */
  private buildModalityOptions(): void {
    const allowMaster = this.auth.isGod() || this.course?.modality === CourseModality.MASTER;
    const modalities = [CourseModality.STANDARD, CourseModality.FLASH, CourseModality.FP, ...(allowMaster ? [CourseModality.MASTER] : [])];
    this.courseModalityOptions = modalities.map((value) => ({ value, viewValue: this.translate.instant(MODALITY_LABEL_KEYS_BY_VALUE[value]) }));
  }

  async initForm(): Promise<void> {
    try {
      const courseId = (this.data && this.data.courseId) || null;
      this.editMode = courseId ? true : false;

      let course: any = null;

      if (courseId) {
        const res = await firstValueFrom(this.api.fetchCourseBasicData(courseId));
        course = res.course;
        this.course = course;
      }
      // ENF-2372 v2: la modalidad y la raíz no cambian en un curso ya creado; una edición solo se crea desde
      // «Crear nueva edición» (B.6), que llega con la raíz preseleccionada.
      const editionData: CourseEditionData = course ?? {};
      const presetRootCourseId: string | null = !this.editMode && this.isGod ? this.data?.presetPredecessorCourseId || null : null;
      const storedPredecessorId = editionData.predecessorCourseId ?? null;
      const isStoredMaster = course?.modality === CourseModality.MASTER;
      this.isCreatingEdition = !!presetRootCourseId;
      this.modalityLocked = this.editMode || !!presetRootCourseId;
      this.previousModality = presetRootCourseId ? CourseModality.MASTER : (course?.modality ?? null);
      // Una edición guardada genera su nombre con el nombre del Máster de su raíz. `GET /course/basic` solo envía
      // `masterName` en la raíz: el de una edición sale del nombre de su raíz sin el sufijo.
      this.editionRootName =
        isStoredMaster && storedPredecessorId ? (editionData.masterName ?? stripEditionSuffix(editionData.predecessor?.name)) : null;
      // La raíz edita el nombre del Máster; el servidor genera «{masterName} - 1ª Edición».
      const initialMasterName = isStoredMaster && !storedPredecessorId ? (editionData.masterName ?? stripEditionSuffix(course?.name)) : null;
      this.buildModalityOptions();

      this.originalYearsCount = course?.yearsCount ?? null;

      if (course?.competenciesFileUrl) {
        const urlFileName = decodeURIComponent(String(course.competenciesFileUrl).split('/').pop() || '');
        this.competenciesInitialFile = { name: urlFileName || 'competencias.pdf', url: course.competenciesFileUrl };
      }

      const isFlashCourse = course?.modality === CourseModality.FLASH;
      this.initialGenerateAutoevaluation = course?.generateAutoevaluation ?? true;

      this.courseForm = new FormGroup({
        courseId: new FormControl(course?.courseId || uuidv4(), [Validators.required]),
        name: new FormControl(course?.name || null, [Validators.required, maxCodePointsValidator(COURSE_NAME_MAX_LENGTH)]),
        masterName: new FormControl<string | null>(initialMasterName),
        shortDescription: new FormControl(course?.shortDescription || null, Validators.required),
        description: new FormControl(course?.description || null, Validators.required),
        discord: new FormControl(course?.discord || null),
        whatsapp: new FormControl(course?.whatsapp || null),
        activeCampaignTagId: new FormControl(course?.activeCampaignTagId || null),
        activeCampaignListId: new FormControl(course?.activeCampaignListId || null),
        hotmartProductId: new FormControl(course?.hotmartProductId || null),
        requiresDocuments: new FormControl(course?.requiresDocuments || null),
        requiredDocuments: new FormControl(course?.requiredDocuments || null),
        requiresDni: new FormControl(isFlashCourse ? false : course?.requiresDni || false),
        requiresDegree: new FormControl(isFlashCourse ? false : course?.requiresDegree || false),
        equivalentMasterId: new FormControl(!isFlashCourse && course?.requiresDegree ? course?.equivalentMasterId || null : null),
        equivalentCourseId: new FormControl(!isFlashCourse && course?.requiresDegree ? course?.equivalentCourseId || null : null),
        sendDiploma: new FormControl(course?.sendDiploma || false, Validators.required),
        attachCompetencies: new FormControl(course?.attachCompetencies || false, this.competenciesFileRequiredValidator),
        competenciesFileUrl: new FormControl(course?.competenciesFileUrl || null),
        universaeEndpoint: new FormControl(course?.universaeEndpoint || null),

        duration: new FormControl(course?.duration || null),
        modality: new FormControl(
          { value: presetRootCourseId ? CourseModality.MASTER : course?.modality || null, disabled: this.modalityLocked },
          Validators.required
        ),
        yearsCount: new FormControl(course?.yearsCount || 1),
        courseImageUrl: new FormControl(course?.courseImageUrl || null),

        accreditationCenter: new FormControl(course?.accreditationCenter || null),
        socialMediaRedirectUrl: new FormControl(course?.socialMediaRedirectUrl || null, [permissiveUrlValidator]),
        landingUrl: new FormControl(course?.landingUrl || null, [httpsOnlyUrlValidator]),
        generateEnrollmentOrder: new FormControl(course?.generateEnrollmentOrder || false, Validators.required),
        generateAutoevaluation: new FormControl(this.initialGenerateAutoevaluation, Validators.required),
        requiresTfm: new FormControl(course?.requiresTfm || false),
        autoMessagesEnabled: new FormControl(course?.autoMessagesEnabled || false, Validators.required),

        promotionContent: new FormControl(course?.promotionContent || null),
        showPromotionContent: new FormControl(course?.showPromotionContent || null),
        showPromotionComments: new FormControl(course?.showPromotionComments || null),

        isAmbassadorProgramEnabled: new FormControl(course?.isAmbassadorProgramEnabled || false, Validators.required),
        ambassadorCommissionType: new FormControl(course?.ambassadorCommissionType || EmbassadorComissionType.PERCENTAGE),
        ambassadorCommissionValue: new FormControl(course?.ambassadorCommissionValue || 10),

        // Read-only after creation (v2, D2): the switch is shown checked and locked in an edition, never editable.
        isSuccessor: new FormControl(!!storedPredecessorId || !!presetRootCourseId),
        predecessorCourseId: new FormControl(storedPredecessorId || presetRootCourseId || null),
        editionNumber: new FormControl<number | string | null>(editionData.editionNumber ?? null),
        purchaseValidFrom: new FormControl<EditionDateValue>(utcIsoToPickerValue(editionData.purchaseValidFrom)),
        purchaseValidUntil: new FormControl<EditionDateValue>(utcIsoToPickerValue(editionData.purchaseValidUntil)),

        pipelineId: new FormControl('', {
          validators: [Validators.pattern(/^\d+$/)],
          asyncValidators: [pipelineAsyncValidator(this.kommoService)],
          updateOn: 'blur'
        }),

        courseMessages: new FormArray<
          FormGroup<{
            active: FormControl<boolean>;
            messageId: FormControl<string>;
            offset: FormControl<number | null>;
          }>
        >([])
      });

      // Only God loads the WhatsApp messages (a tutor neither sees nor sends them)
      if (this.isGod) {
        this.loadCourseMessages();
      }

      if (course && course.teachersAndTutors && course.teachersAndTutors.length > 0) {
        await this.loadCourseTeachers(course.teachersAndTutors);
      }

      if (course && course.contentDirectors && course.contentDirectors.length > 0) {
        await this.loadCourseContentDirectors(course.contentDirectors);
      }

      // The Kommo pipeline is God only (its endpoints answer 403 to a tutor): the tutor never asks for it.
      if (courseId && this.isGod) {
        const pipelineRes = await firstValueFrom(this.kommoService.getCoursePipeline(courseId));
        if (pipelineRes.success && pipelineRes.data) {
          this.courseForm.get('pipelineId')?.setValue(String(pipelineRes.data.kommoPipelineId));
        }
      }

      // «Crear nueva edición» (B.6, D-B2): the root is known before the form is painted, so its master name shows
      // from the first render. `edition-defaults` is asked once here and reused for the number and the prefill below.
      const presetDefaults = presetRootCourseId ? await this.fetchPresetEditionDefaults(presetRootCourseId) : null;
      if (presetDefaults) {
        this.editionRootName = this.masterNameOf(presetDefaults);
      }

      this.updateMasterNameValidators();
      this.updateEditionValidators();

      this.isLoadingCourse = false;
      this.listenForChanges();
      this.listenForPipelineValidation();
      this.listenForEditionChanges();

      if (presetRootCourseId) {
        void this.loadEditionDefaults(presetRootCourseId, presetDefaults);
      }
    } catch (error) {
      this.isLoadingCourse = false;
      this.toast.presentErrorToast(isNetworkError(error) ? this.translate.instant(NETWORK_ERROR_KEY) : 'Hubo un error consultando curso');
    }
  }

  private loadCourseMessages(): void {
    const fullCourseMessages = this.allMessages.map((message) => {
      const existingCourseMessage = this.course?.courseMessages?.find((cm: any) => cm.message.messageId === message.messageId);
      const hasBotConfigured = !!message.kommoBotId;

      return {
        messageId: message.messageId,
        active: hasBotConfigured && existingCourseMessage ? true : false,
        offset: existingCourseMessage ? existingCourseMessage.offset : message.offset || null,
        hasBotConfigured
      };
    });

    const formGroups = fullCourseMessages.map(
      (cm) =>
        new FormGroup({
          messageId: new FormControl(cm.messageId, Validators.required),
          active: new FormControl({ value: cm.active, disabled: !cm.hasBotConfigured }, Validators.required),
          offset: new FormControl({ disabled: !cm.active, value: cm.offset })
        })
    );

    for (const fg of formGroups) {
      fg.controls.active.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((isActive) => {
        const message = this.allMessages.find((msg) => msg.messageId === fg.controls.messageId.value);
        if (!isActive) {
          fg.controls.offset.setValue(message ? message.offset || null : null);
          fg.controls.offset.disable();
        } else {
          fg.controls.offset.enable();
        }
      });
      fg.controls.offset.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((v) => {
        if (v < 1 || v === null || v === '') {
          fg.controls.offset.setValue(1);
        }
        this.cdr.detectChanges();
      });
    }
    this.courseForm.setControl('courseMessages', new FormArray(formGroups));
  }

  listenForChanges(): void {
    this.courseForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.markAsModified());

    // The modality only changes while creating a course (it is locked afterwards, v2 D2).
    this.modalityFC.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      // ENF-2372 v2: a new Máster is its own root and the field becomes «Nombre del Máster»; what was typed as the
      // course name moves there, and back when leaving Máster.
      const wasMaster = this.previousModality === CourseModality.MASTER;
      this.previousModality = value;
      this.updateMasterNameValidators();
      if (value === CourseModality.MASTER && !wasMaster) {
        this.masterNameFC.setValue(this.nameFC.value?.trim() || null);
      } else if (value !== CourseModality.MASTER && wasMaster) {
        this.nameFC.setValue(this.masterNameFC.value);
      }
      // The validity shows/hides with the modality: so does its validation (O-E1).
      this.updateEditionValidators();

      if (value === CourseModality.FLASH) {
        this.courseForm.patchValue(
          {
            requiresDni: false,
            requiresDegree: false,
            equivalentMasterId: null,
            equivalentCourseId: null
          },
          { emitEvent: false }
        );
        this.durationFC.setValue(null);
        this.durationFC.setValidators([Validators.required, Validators.min(1)]);
      }
      if (value !== CourseModality.FLASH) {
        this.durationFC.setValue(null);
        this.durationFC.clearValidators();
      }
      this.durationFC.updateValueAndValidity();

      // ENF-2266: los cursos flash quedan con autoevaluación desmarcada por defecto,
      // el resto marcada, cada vez que cambia la modalidad.
      this.courseForm.get('generateAutoevaluation')?.setValue(value !== CourseModality.FLASH);

      // ENF-2258: los cursos flash no ofrecen la opción de enviar el diploma por correo.
      if (value === CourseModality.FLASH) {
        this.courseForm.get('sendDiploma')?.setValue(false);
      }

      if (value === CourseModality.FP) {
        this.yearsCountFC.setValue(1);
        this.yearsCountFC.setValidators([Validators.min(1)]);
      } else {
        this.yearsCountFC.setValue(null);
        this.yearsCountFC.clearValidators();
      }
      this.yearsCountFC.updateValueAndValidity();
    });
    this.durationFC.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged()).subscribe((value) => {
      if (value && value < 1) {
        this.durationFC.setValue(1);
      }
    });

    this.courseForm
      .get('requiresDegree')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((requiresDegree) => {
        if (!requiresDegree) {
          this.courseForm.patchValue({ equivalentMasterId: null, equivalentCourseId: null }, { emitEvent: false });
        }
      });
    this.yearsCountFC.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged()).subscribe((value) => {
      if (value && value < 1) {
        this.yearsCountFC.setValue(1);
      }
    });

    // Listener for commission type changes
    this.ambassadorCommissionTypeFC.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((type) => {
      this.updateCommissionValidators(type);

      // Adjust value according to commission type
      const currentValue = this.ambassadorCommissionValueFC.value;
      if (currentValue !== null && currentValue !== '') {
        if (type === EmbassadorComissionType.PERCENTAGE) {
          // If changed to percentage, remove decimals
          const roundedValue = Math.round(currentValue);
          if (roundedValue !== currentValue) {
            this.ambassadorCommissionValueFC.setValue(roundedValue, { emitEvent: false });
          }

          // Force validation to show errors if value is not valid for percentage
          this.ambassadorCommissionValueFC.markAsTouched();
          this.ambassadorCommissionValueFC.updateValueAndValidity();
        }
        // If changed to fixed, do nothing (keep the value)
      }
    });

    // Listener for real-time commission value validation
    this.ambassadorCommissionValueFC.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged()).subscribe((value) => {
      const commissionType = this.ambassadorCommissionTypeFC.value;
      if (commissionType === EmbassadorComissionType.PERCENTAGE && value !== null && value !== '') {
        // For percentages, automatically round if there are decimals
        const roundedValue = Math.round(value);
        if (roundedValue !== value) {
          this.ambassadorCommissionValueFC.setValue(roundedValue, { emitEvent: false });
        }
      }
    });

    // Initialize validators when loading the form
    const initialCommissionType = this.ambassadorCommissionTypeFC.value;
    this.updateCommissionValidators(initialCommissionType);
  }

  private updateCommissionValidators(type: EmbassadorComissionType): void {
    const control = this.ambassadorCommissionValueFC;

    if (type === EmbassadorComissionType.PERCENTAGE) {
      control.setValidators([Validators.required, ModalEditCourseComponent.percentageCommissionValidator]);
    } else if (type === EmbassadorComissionType.FIXED) {
      control.setValidators([Validators.required, ModalEditCourseComponent.fixedCommissionValidator]);
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  /**
   * Single entry point of «Guardar». A second click while a save is still running (before `isSavingCourse` disables
   * the button: the confirmations and checks above it are async) is ignored, so a new edition is never sent twice.
   */
  async saveCourse(): Promise<void> {
    if (this.saveInProgress) return;
    this.saveInProgress = true;
    try {
      await this.runSaveCourse();
    } finally {
      this.saveInProgress = false;
    }
  }

  private async runSaveCourse(): Promise<void> {
    const attachCompetenciesFC = this.courseForm.get('attachCompetencies');
    this.revalidateAttachCompetencies();
    if (attachCompetenciesFC?.hasError('competenciesFileRequired')) {
      attachCompetenciesFC.markAsTouched();
      this.toast.presentErrorToast('Sube el PDF de competencias o desmarca "Adjuntar competencias en el diploma"');
      return;
    }

    // ENF-2266: al editar un curso, cambiar la autoevaluación pide confirmación
    // porque afecta a lo que ven los alumnos en cada lección.
    if (this.editMode) {
      const newGenerateAutoevaluation = this.courseForm.get('generateAutoevaluation')?.value;
      if (newGenerateAutoevaluation !== this.initialGenerateAutoevaluation) {
        const confirmed = newGenerateAutoevaluation
          ? await this.alert.presentConfirmActionAlert(
              'Activar autoevaluación',
              'Si activas la autoevaluación para este curso, se mostrará a los alumnos las preguntas de autoevaluación para cada lección que completen.',
              'Cancelar',
              'Activar'
            )
          : await this.alert.presentConfirmActionAlert(
              'Desactivar autoevaluación',
              'Si desactivas la autoevaluación para este curso, ya no se mostrarán a los alumnos las preguntas de autoevaluación para cada lección que completen.',
              'Cancelar',
              'Desactivar'
            );
        if (!confirmed) return;
      }
    }

    // ENF-2372: los errores del servidor de un guardado anterior no deben bloquear el siguiente intento.
    this.clearEditionServerErrors();

    if (!this.validateEditionFields()) return;

    // FP year reduction (US A.3): a lower yearsCount than before deletes the
    // removed years' content in cascade — check first and let the admin cancel
    // instead of deleting on the first submit.
    if (this.editMode && this.isFP && this.originalYearsCount && this.yearsCountFC.value < this.originalYearsCount) {
      const courseId = this.courseForm.get('courseId')?.value;
      let preview: any;
      try {
        preview = await firstValueFrom(this.api.fetchCourseYearReductionPreview(courseId, this.yearsCountFC.value));
      } catch {
        this.toast.presentErrorToast('No se pudo comprobar el contenido de los años a eliminar. Inténtalo de nuevo.');
        return;
      }
      if (preview?.hasContent) {
        const confirmed = await this.alert.presentConfirmRemoveActionAlert({
          title: 'Reducir años del curso',
          message: `Vas a reducir el curso de ${this.originalYearsCount} a ${this.yearsCountFC.value} años.`,
          message2: 'El contenido de los años eliminados se borrará permanentemente. Esta acción no se puede deshacer.',
          cancelText: 'Cancelar',
          confirmText: 'Eliminar años y contenido'
        });
        if (!confirmed) return;
      }
    }

    try {
      this.isSavingCourse = true;

      const courseFormData = new FormData();

      // Prepare course data
      const isFlashCourse = this.isFlash;
      const requiresDni = !isFlashCourse && this.courseForm.get('requiresDni')?.value === true;
      const requiresDegree = !isFlashCourse && this.courseForm.get('requiresDegree')?.value === true;
      // Los campos de edición se serializan aparte (fechas en UTC, solo God); el resto tal cual.
      // `courseMessages`, `autoMessagesEnabled` and `pipelineId` are God only: they are taken out of the form value and
      // sent below only for God, so a tutor never sends (nor changes) them.
      const {
        editionNumber: _editionNumber,
        purchaseValidFrom: _validFrom,
        purchaseValidUntil: _validUntil,
        courseMessages: _courseMessages,
        autoMessagesEnabled: _autoMessagesEnabled,
        pipelineId: _pipelineId,
        masterName: _masterName,
        ...formValue
      } = this.courseForm.value;
      const courseData: any = {
        ...formValue,
        // El servidor guarda el nombre recortado (D-07); se envía ya recortado para que no cuente espacios.
        name: typeof formValue.name === 'string' ? formValue.name.trim() : formValue.name,
        // `modality` está deshabilitada en un curso ya creado (v2) y `value` la omitiría.
        modality: this.modalityFC.value,
        ...(this.isGod ? this.buildEditionPayload() : {}),
        requiresDni,
        requiresDegree,
        equivalentMasterId: requiresDegree ? this.courseForm.get('equivalentMasterId')?.value || null : null,
        equivalentCourseId: requiresDegree ? this.courseForm.get('equivalentCourseId')?.value || null : null,
        teachers: this.selectedTeachersAndTutors.map((t) => t.id),
        contentDirectors: this.selectedContentDirectors.map((cd) => cd.id)
      };

      // Only God sends courseMessages and autoMessagesEnabled: this keeps the stored ones when a tutor edits the course.
      // A new edition sends none either: it inherits the messages of its root (C-6) and the server would discard them.
      if (this.isGod && !this.isCreatingEdition) {
        courseData.courseMessages = this.courseMessagesControls
          .filter((cm) => cm.get('active')?.value)
          .map((cm) => ({
            messageId: cm.get('messageId')?.value,
            offset: cm.get('offset')?.value
          }));
        courseData.autoMessagesEnabled = this.courseForm.get('autoMessagesEnabled')?.value;
      }

      const body: any = {
        course: courseData
      };

      courseFormData.append('form', JSON.stringify(body));
      if (this.imageToUpload) {
        courseFormData.append('courseImage', this.imageToUpload, this.imageToUpload.name);
      }
      if (this.competenciesFileToUpload) {
        courseFormData.append('competenciesFile', this.competenciesFileToUpload, this.competenciesFileToUpload.name);
      }

      const response = await firstValueFrom(this.api.saveCourseBasicData(courseFormData));

      const courseId = this.courseForm.get('courseId')?.value;
      const pipelineId = this.courseForm.get('pipelineId')?.value;
      // Only God saves the Kommo pipeline: a tutor never touches it (the endpoints are God only).
      if (this.isGod) {
        if (courseId && pipelineId && this.courseForm.get('pipelineId')?.valid) {
          this.kommoService.saveCoursePipeline(courseId, Number(pipelineId)).subscribe();
        } else if (courseId && !pipelineId && this.editMode) {
          this.kommoService.deleteCoursePipeline(courseId).subscribe();
        }
      }

      this.isSavingCourse = false;
      this.closeModal({ courseUpdated: response.courseUpdated });

      const message = this.editMode ? '¡El curso se ha guardado con éxito!' : '¡El curso se ha creado con éxito! Añade contenido a tu nuevo curso';
      this.toast.presentCustomToast(
        message,
        {
          action: {
            text: 'Añadir contenido',
            callback: () => {
              const courseId = this.courseForm.get('courseId')?.value;
              this.router.navigate(['/god/courses/content', courseId]);
            }
          },
          duration: 10000
        },
        'success'
      );
    } catch (errorResponse: any) {
      this.isSavingCourse = false;
      // Sin respuesta del servidor (D-09): aviso traducido; el formulario se queda abierto con sus datos.
      if (isNetworkError(errorResponse)) {
        this.toast.presentErrorToast(this.translate.instant(NETWORK_ERROR_KEY));
        return;
      }
      const serverError: CourseFieldError = errorResponse?.error ?? {};
      const errorMessage = serverError.message;

      // Otro admin borró el curso con el formulario abierto (QA D-04): no hay nada que guardar; se cierra el modal y
      // quien lo abrió recarga el listado (`courseGone`).
      if (isCourseDeletedError(serverError)) {
        this.toast.presentErrorToast(this.translate.instant('COURSE.EDITION.ERRORS.COURSE_DELETED'));
        this.closeModal({ courseGone: true });
        return;
      }

      // Errores de validación de un campo concreto (predecesor, ENF-2170; número,
      // vigencia y nombre, ENF-2372) se muestran en el propio campo, no en un toast.
      const fieldControl = this.getInlineErrorControl(serverError.field);
      if (fieldControl) {
        fieldControl.setErrors({ serverError: errorMessage || this.translate.instant('COURSE.EDITION.ERRORS.FIELD_INVALID') });
        fieldControl.markAsTouched();
        return;
      }

      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando curso');
    }
  }

  /**
   * ENF-2372: campos de edición que se envían (solo God, regla 11): el nombre del Máster en una raíz, el número en una
   * edición y las fechas al editar (para conservarlas) o en altas si el bloque de vigencia está visible.
   */
  private buildEditionPayload(): Partial<CourseEditionPayload> {
    const payload: Partial<CourseEditionPayload> = {};
    // The root names the whole Máster (v2): the server builds «{masterName} - 1ª Edición» and, on an existing root,
    // renames every edition. Only God sends it (a tutor sees it read-only).
    if (this.isMasterRoot) {
      payload.masterName = (this.masterNameFC.value ?? '').trim();
    }
    // Only while the number field is on screen (QA-A-01): an older successor that is not a Máster (FP/Flash with a
    // predecessor) has no number and no way to type one; leaving the field out keeps its relation untouched.
    if (this.isEditionNumberVisible) {
      payload.editionNumber = parseEditionNumber(this.editionNumberFC.value);
    }
    if (this.editMode || this.showEditionValidity) {
      payload.purchaseValidFrom = pickerValueToUtcIso(this.validFromFC.value);
      payload.purchaseValidUntil = pickerValueToUtcIso(this.validUntilFC.value);
    }
    return payload;
  }

  /** Control donde se pinta un error `{ field }` del servidor; `null` = se muestra en un toast. */
  private getInlineErrorControl(field: string | undefined): AbstractControl | null {
    if (!field) return null;
    // A root shows «Nombre del Máster» instead of the course name: its name errors go there.
    if (field === 'masterName' || (field === 'name' && this.isMasterRoot)) return this.isMasterRoot ? this.masterNameFC : this.nameFC;
    if (field === 'name') return this.nameFC;
    // The root (`predecessorCourseId`/`rootCourseId`) and the modality are read-only: their errors go to a toast.
    // Los campos de número y vigencia solo los ve God (D-10); para otro rol, toast.
    const editionFields = ['editionNumber', 'purchaseValidFrom', 'purchaseValidUntil'];
    return this.isGod && editionFields.includes(field) ? this.courseForm.get(field) : null;
  }

  private clearEditionServerErrors(): void {
    for (const control of [this.nameFC, this.masterNameFC, this.editionNumberFC, this.validFromFC, this.validUntilFC]) {
      if (control?.hasError('serverError')) control.updateValueAndValidity();
    }
  }

  /** Validación en cliente de número, vigencia y longitud del nombre (A.2, A.3, A.8). */
  private validateEditionFields(): boolean {
    const invalidControls: AbstractControl[] = [this.editionNumberFC, this.validFromFC, this.validUntilFC].filter((control) => control.invalid);
    if (this.isMasterRoot && this.masterNameFC.invalid) invalidControls.push(this.masterNameFC);
    else if (this.nameFC.hasError('maxlength')) invalidControls.push(this.nameFC);
    if (!invalidControls.length) return true;

    invalidControls.forEach((control) => control.markAsTouched());
    this.toast.presentErrorToast(this.translate.instant('COURSE.EDITION.ERRORS.FIX_FIELDS'));
    return false;
  }

  /**
   * Validadores que dependen de si el curso es edición: número obligatorio y vigencia
   * obligatoria solo al crear una edición nueva. Solo aplican a God (los demás no ven
   * ni pueden cambiar estos campos).
   */
  private updateEditionValidators(): void {
    const validityRequired = this.isGod && this.editionValidityRequired;
    const rangeValidator = validityRangeValidator('purchaseValidFrom');
    // Same rule for the dates (O-E1): validated only while the validity block is on screen.
    const yearValidators = this.showEditionValidity ? [validityYearValidator] : [];
    const untilValidators = this.showEditionValidity ? [rangeValidator, ...yearValidators] : [];

    // The number is validated only while its field is visible (QA-A-01): a hidden field cannot show its error and
    // would block the save with «Revisa los campos marcados» and nothing marked.
    this.editionNumberFC.setValidators(this.isEditionNumberVisible ? [Validators.required, editionNumberValidator] : []);
    this.validFromFC.setValidators(validityRequired ? [Validators.required, ...yearValidators] : yearValidators);
    this.validUntilFC.setValidators(validityRequired ? [Validators.required, ...untilValidators] : untilValidators);
    for (const control of [this.editionNumberFC, this.validFromFC, this.validUntilFC]) {
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * «Nombre del Máster» se valida solo en una raíz y solo para God (quien lo puede cambiar): obligatorio y de 86
   * caracteres como mucho, para que quepa « - nª Edición» (v2, D1).
   */
  private updateMasterNameValidators(): void {
    const validators = this.isMasterRoot && this.canEditMasterName ? [Validators.required, maxCodePointsValidator(MASTER_NAME_MAX_LENGTH)] : [];
    this.masterNameFC.setValidators(validators);
    this.masterNameFC.updateValueAndValidity({ emitEvent: false });
  }

  /** Raíz: el nombre del curso es siempre `{masterName} - 1ª Edición` (el servidor lo vuelve a generar). */
  private syncRootName(): void {
    if (!this.isMasterRoot) return;
    const masterName = this.masterNameFC.value?.trim();
    this.nameFC.setValue(masterName ? buildEditionName(masterName, 1) : null);
  }

  /** Nombre del Máster de una raíz según `edition-defaults` (respaldo: su nombre completo sin el sufijo). */
  private masterNameOf(defaults: CourseEditionDefaults): string | null {
    return defaults.masterName ?? stripEditionSuffix(defaults.rootName);
  }

  /** Regenera `{masterName} - {n}ª Edición` al cambiar el número (A.8). El servidor lo recalcula igualmente. */
  private regenerateEditionName(): void {
    const editionNumber = parseEditionNumber(this.editionNumberFC.value);
    if (!this.isEditionCourse || !this.editionRootName || !isPositiveInteger(editionNumber)) return;
    this.nameFC.setValue(buildEditionName(this.editionRootName, editionNumber));
    // Que el aviso de longitud (> 100) se vea sin esperar a que se toque el campo.
    this.nameFC.markAsTouched();
  }

  /**
   * Marcar "Adjuntar competencias" sin PDF no produce nada: el backend genera el
   * diploma sin la hoja y nadie se entera. El fichero puede ser uno ya guardado
   * (`competenciesFileUrl`) o uno recién elegido pendiente de subir.
   */
  private readonly competenciesFileRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const hasFile = !!this.competenciesFileToUpload || !!control.parent?.get('competenciesFileUrl')?.value;
    return hasFile ? null : { competenciesFileRequired: true };
  };

  get competenciesError(): string {
    const control = this.courseForm?.get('attachCompetencies');
    if (control?.touched && control?.hasError('competenciesFileRequired')) {
      return 'Sube el PDF de competencias o desmarca la casilla';
    }
    return '';
  }

  /** El validador depende de `competenciesFileToUpload`, que no es un control: hay que relanzarlo a mano. */
  private revalidateAttachCompetencies(): void {
    this.courseForm?.get('attachCompetencies')?.updateValueAndValidity({ emitEvent: false });
  }

  onCompetenciesFileSelected(pdf: PdfFile): void {
    this.competenciesFileToUpload = pdf.file;
    this.revalidateAttachCompetencies();
    this.markAsModified();
  }

  onCompetenciesFileRemoved(): void {
    this.competenciesFileToUpload = null;
    this.courseForm.get('competenciesFileUrl')?.setValue(null);
    // Quitar el PDF implica que ya no hay nada que adjuntar: se desmarca en vez de
    // dejar el formulario en un estado que el guardado va a rechazar.
    this.courseForm.get('attachCompetencies')?.setValue(false);
    this.revalidateAttachCompetencies();
  }

  uploadImage(data: { imageToUpload: File | null; imageToUploadSrc: string | null }) {
    this.imageToUpload = data.imageToUpload;
    this.imageToUploadSrc = data.imageToUploadSrc;
    this.markAsModified();
  }

  /**
   * Habilita "Guardar" y el aviso al cerrar. Además de los cambios del formulario, lo llaman
   * los cambios que viven fuera de `courseForm` (imagen, PDF de competencias, profesores,
   * directores): no emiten `valueChanges` y el botón se quedaba deshabilitado.
   */
  private markAsModified(): void {
    this.showCloseAlert = true;
    this.formModified = true;
  }

  deleteImage() {
    this.imageToUpload = null;
    this.imageToUploadSrc = null;
    this.courseForm.patchValue({
      courseImageUrl: null
    });
    this.courseForm.get('courseImageUrl')?.updateValueAndValidity();
    this.course = {
      ...this.course,
      courseImageUrl: null
    };
  }

  fetchTeachers = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      firstValueFrom(
        this.api.fetchAvailableTeacherAndTutors({
          page: page + 1,
          limit: pageSize,
          search: search
        })
      )
        .then((response) => {
          const mappedData: SelectOption[] =
            response.teachersAndTutors?.map((teacher: any) => ({
              id: teacher.userId,
              title: `${teacher.name ? teacher.name + ' ' : ''}${teacher.lastname ? teacher.lastname + ' ' : ''}${teacher.role === Role.Tutor ? '(' + formatRole(teacher.role) + ')' : ''}`,
              selected: false
            })) || [];

          observer.next({
            data: mappedData,
            total: response.pagination?.total || mappedData.length
          });
          observer.complete();
        })
        .catch((error) => {
          console.error('Error fetching teachers:', error);
          observer.next({
            data: [],
            total: 0
          });
          observer.complete();
        });
    });
  };

  fetchContentDirectors = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      firstValueFrom(
        this.api.fetchAvailableContentDirectors({
          page: page + 1,
          limit: pageSize,
          search: search
        })
      )
        .then((response) => {
          const mappedData: SelectOption[] =
            response.contentDirectors?.map((director: any) => ({
              id: director.userId,
              title: `${director.name ? director.name + ' ' : ''}${director.lastname ? director.lastname + ' ' : ''}`,
              selected: false
            })) || [];

          observer.next({
            data: mappedData,
            total: response.pagination?.total || mappedData.length
          });
          observer.complete();
        })
        .catch((error) => {
          console.error('Error fetching content directors:', error);
          observer.next({
            data: [],
            total: 0
          });
          observer.complete();
        });
    });
  };

  async loadCourseTeachers(teacherAndTutorIds: string[]): Promise<void> {
    if (!teacherAndTutorIds || teacherAndTutorIds.length === 0) {
      this.selectedTeachersAndTutors = [];
      return;
    }

    try {
      const response = await firstValueFrom(
        this.api.fetchAvailableTeacherAndTutors({
          search: ''
        })
      );

      if (response.teachersAndTutors) {
        const courseTeachersAndTutors = response.teachersAndTutors
          .filter((teacherOrTutor: any) => teacherAndTutorIds.includes(teacherOrTutor.userId))
          .map((teacherOrTutor: any) => ({
            id: teacherOrTutor.userId,
            title: `${teacherOrTutor.name ? teacherOrTutor.name + ' ' : ''}${teacherOrTutor.lastname ? teacherOrTutor.lastname + ' ' : ''}${teacherOrTutor.role === Role.Tutor ? '(' + formatRole(teacherOrTutor.role) + ')' : ''}`,
            selected: true
          }));

        this.selectedTeachersAndTutors = courseTeachersAndTutors;
      }
    } catch (error) {
      console.error('Error loading course teachers:', error);
      this.selectedTeachersAndTutors = teacherAndTutorIds.map((id) => ({
        id: id,
        title: `Teacher ID: ${id}`,
        selected: true
      }));
    }
  }

  async loadCourseContentDirectors(contentDirectorIds: string[]): Promise<void> {
    if (!contentDirectorIds || contentDirectorIds.length === 0) {
      this.selectedContentDirectors = [];
      return;
    }

    try {
      const response = await firstValueFrom(
        this.api.fetchAvailableContentDirectors({
          search: ''
        })
      );

      if (response.contentDirectors) {
        const courseContentDirectors = response.contentDirectors
          .filter((director: any) => contentDirectorIds.includes(director.userId))
          .map((director: any) => ({
            id: director.userId,
            title: `${director.name ? director.name + ' ' : ''}${director.lastname ? director.lastname + ' ' : ''}`,
            selected: true
          }));

        this.selectedContentDirectors = courseContentDirectors;
      }
    } catch (error) {
      console.error('Error loading course content directors:', error);
      this.selectedContentDirectors = contentDirectorIds.map((id) => ({
        id: id,
        title: `Director ID: ${id}`,
        selected: true
      }));
    }
  }

  removeTeacher(teacher: SelectOption): void {
    this.selectedTeachersAndTutors = this.selectedTeachersAndTutors.filter((t) => t.id !== teacher.id);
    this.markAsModified();
  }

  removeContentDirector(director: SelectOption): void {
    this.selectedContentDirectors = this.selectedContentDirectors.filter((d) => d.id !== director.id);
    this.markAsModified();
  }

  getMessage(messageId: string): any {
    return this.allMessages.find((message: any) => message.messageId === messageId);
  }

  handleCourseMessageOpened(_messageId: string): void {
    // No-op: bot info is displayed directly from the message model
  }

  /**
   * Listen for pipeline async validation status changes.
   * When pipeline is invalid or empty, auto-disable autoMessagesEnabled and prevent enabling it.
   */
  private listenForPipelineValidation(): void {
    const pipelineCtrl = this.courseForm.get('pipelineId');
    const autoMsgCtrl = this.courseForm.get('autoMessagesEnabled');
    if (!pipelineCtrl || !autoMsgCtrl) return;

    const updateAutoMessages = () => {
      // Skip PENDING state to avoid clobbering the saved value while async validation is in flight
      if (pipelineCtrl.status === 'PENDING') return;

      const isPipelineValid = pipelineCtrl.valid && !!pipelineCtrl.value;
      if (!isPipelineValid) {
        autoMsgCtrl.setValue(false, { emitEvent: false });
        autoMsgCtrl.disable({ emitEvent: false });
      } else {
        autoMsgCtrl.enable({ emitEvent: false });
      }
    };

    // Check on initial load
    updateAutoMessages();

    // React to status changes (VALID, INVALID, PENDING)
    pipelineCtrl.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      updateAutoMessages();
    });

    // Also react to value changes: when the field is cleared the status stays VALID
    // (async validator short-circuits for empty), so statusChanges never fires.
    pipelineCtrl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      updateAutoMessages();
    });
  }

  /**
   * ENF-2372 v2: the root of an edition never changes after it is created (a wrong one is deleted and recreated), so
   * only the edition number (which regenerates the name, A.8) and, in a root, the master name are live.
   */
  private listenForEditionChanges(): void {
    this.editionNumberFC.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe(() => this.regenerateEditionName());
    this.masterNameFC.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.syncRootName());
    this.validFromFC.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.validUntilFC.updateValueAndValidity());
  }

  /**
   * «Crear nueva edición»: proposed number and master name of the root (A.2/A.8) and the prefill of the general data
   * from the ROOT (PO option A, 15-oct; the server copies the syllabus from the last edition).
   */
  private async loadEditionDefaults(rootCourseId: string, knownDefaults: CourseEditionDefaults | null = null): Promise<void> {
    try {
      this.isLoadingPredecessorPrefill = true;
      const defaults = knownDefaults ?? (await firstValueFrom(this.api.fetchEditionDefaults(rootCourseId)));
      this.editionRootName = this.masterNameOf(defaults);

      await this.prefillFromRoot(defaults.rootCourseId ?? rootCourseId);

      // After the prefill, so nothing overwrites it; regenerates the name. A number typed while loading is kept.
      if (!this.editionNumberFC.dirty) {
        this.editionNumberFC.setValue(defaults.nextEditionNumber);
      }
      this.regenerateEditionName();
    } catch (error) {
      console.error('Error loading edition defaults:', error);
      // No answer from the server (D-09): translated notice instead of «Failed to fetch»; the form keeps its data.
      if (isNetworkError(error)) {
        this.toast.presentErrorToast(this.translate.instant(NETWORK_ERROR_KEY));
        return;
      }
      // The API explains why the root is not valid (deleted, not a master…).
      const serverError = (error as { error?: CourseFieldError } | null)?.error;
      const serverMessage = typeof serverError?.message === 'string' && serverError.message ? serverError.message : null;
      this.toast.presentErrorToast(serverMessage ?? this.translate.instant('COURSE.EDITION.ERRORS.DEFAULTS_LOAD_FAILED'));
    } finally {
      this.isLoadingPredecessorPrefill = false;
    }
  }

  /**
   * `edition-defaults` of the preset root, asked before the form is painted (D-B2). A failure is not reported here:
   * `loadEditionDefaults` asks again right after and shows the server's reason.
   */
  private async fetchPresetEditionDefaults(rootCourseId: string): Promise<CourseEditionDefaults | null> {
    try {
      return await firstValueFrom(this.api.fetchEditionDefaults(rootCourseId));
    } catch {
      return null;
    }
  }

  /**
   * Prefills the general-data fields of a new edition from its ROOT (ENF-2169; ENF-2372 v2, PO option A: the general
   * data come from the root, the syllabus is copied by the server from the last edition). Copied once: afterwards
   * each edition is independent. The edition's own data (number, purchase validity), the name (generated, A.8), the
   * master name and the modality (already Máster; re-patching it would fire its side effects) are never copied.
   * `competenciesFileUrl`/`attachCompetencies` are excluded on purpose: the
   * file itself is not duplicated client-side, and the backend rejects a
   * competencies URL that does not belong to the course being saved.
   */
  private async prefillFromRoot(rootCourseId: string): Promise<void> {
    try {
      const res = await firstValueFrom(this.api.fetchCourseBasicData(rootCourseId));

      const predecessor = res?.course;
      if (!predecessor) return;

      const {
        courseId: _courseId,
        name: _name,
        predecessor: _predecessorRef,
        predecessorCourseId: _predecessorCourseId,
        competenciesFileUrl: _competenciesFileUrl,
        attachCompetencies: _attachCompetencies,
        modality: _modality,
        type: _type,
        // Each edition has its own integrations (ENF-2372): Hotmart, Universae and ActiveCampaign are never copied.
        hotmartProductId: _hotmartProductId,
        universaeEndpoint: _universaeEndpoint,
        activeCampaignTagId: _activeCampaignTagId,
        activeCampaignListId: _activeCampaignListId,
        editionNumber: _editionNumber,
        purchaseValidFrom: _purchaseValidFrom,
        purchaseValidUntil: _purchaseValidUntil,
        hasEditions: _hasEditions,
        isEdition: _isEdition,
        masterName: _masterName,
        isMasterRoot: _isMasterRoot,
        rootCourseId: _rootCourseId,
        // The course messages are not copied either: an edition without its own uses the ones of its root, live
        // (ENF-2372). Copying them would save them as the edition's own and cut it off from the root.
        courseMessages: _courseMessages,
        autoMessagesEnabled: _autoMessagesEnabled,
        teachersAndTutors,
        contentDirectors,
        ...rest
      } = predecessor;

      // The form is on screen while the root loads: what the admin typed meanwhile (a dirty field) is kept.
      const prefill = Object.fromEntries(Object.entries(rest).filter(([field]) => !this.courseForm.get(field)?.dirty));
      this.courseForm.patchValue(prefill);
      // `modality` is no longer patched (its handler resets yearsCount as a side
      // effect); the year count is still re-applied explicitly so a source's value
      // never depends on the key order of the response.
      if (prefill['yearsCount'] !== undefined) {
        this.yearsCountFC.setValue(prefill['yearsCount']);
      }
      // Keep the img-dragger preview (bound to `course`, not the form) in sync, unless an image was chosen meanwhile.
      if (!this.imageToUpload && 'courseImageUrl' in prefill) {
        this.course = { ...this.course, courseImageUrl: prefill['courseImageUrl'] ?? null };
      }

      // Teachers and content directors chosen while loading are kept too.
      const keepTeachers = this.selectedTeachersAndTutors.length > 0;
      const keepContentDirectors = this.selectedContentDirectors.length > 0;
      await Promise.all([
        keepTeachers ? Promise.resolve() : this.loadCourseTeachers(teachersAndTutors || []),
        keepContentDirectors ? Promise.resolve() : this.loadCourseContentDirectors(contentDirectors || [])
      ]);
    } catch (error) {
      console.error('Error prefilling from the root course:', error);
      this.toast.presentErrorToast(this.translate.instant(isNetworkError(error) ? NETWORK_ERROR_KEY : 'COURSE.EDITION.ERRORS.SOURCE_LOAD_FAILED'));
    }
  }

  // private syncTeachersWithAutocomplete(): void {
  //   if (this.teachersAutocomplete) {
  //     this.teachersAutocomplete.writeValue(this.selectedTeachersAndTutors);
  //   }
  // }

  // private syncContentDirectorsWithAutocomplete(): void {
  //   if (this.contentDirectorAutocomplete) {
  //     this.contentDirectorAutocomplete.writeValue(this.selectedContentDirectors);
  //   }
  // }

  isSendConditionFlashOrInactivity(message: MessageDto): boolean {
    return (
      message.sendCondition === MessageSendCondition.OnUserInactivity || message.sendCondition === MessageSendCondition.OnFlashCourseDeadlineWarning
    );
  }

  getMessageConditionOffsetLabel(message: MessageDto): string {
    if (message.sendCondition === MessageSendCondition.OnFlashCourseDeadlineWarning) {
      return 'Horas';
    }
    if (message.sendCondition === MessageSendCondition.OnUserInactivity) {
      return 'Días';
    }
    return '';
  }

  private mappEmbassadorComissionTypeToViewValue(type: EmbassadorComissionType): string {
    switch (type) {
      case EmbassadorComissionType.FIXED:
        return 'Importe fijo';
      case EmbassadorComissionType.PERCENTAGE:
        return 'Porcentaje';
      default:
        return '';
    }
  }
}
